/**
 * /refund-policy — Compliance Baseline v1.0 (STA-128, STA-129).
 *
 * Establishes the legal basis for waiving the 7-day cooling-off period
 * under 《通訊交易解除權合理例外情事適用準則》第 2 條 — required by
 * NewebPay / 綠界 review and by 《消費者保護法》第 19 條.
 */

import { LegalPage, Section } from '../components/LegalPage';

export default function RefundPolicy() {
  return (
    <LegalPage title="退費政策" version="v1.0" lastUpdated="2026-05-14">
      <Section title="第 1 條　政策摘要（重點先看）">
        <ul>
          <li>✅ <strong>付款前</strong>：您可隨時取消未送出之訂單。</li>
          <li>❌ <strong>付款後且服務已提供</strong>：依法不適用 7 天鑑賞期，無法辦理退費。</li>
          <li>⚠️ <strong>系統錯誤</strong>：因本站技術問題導致服務無法提供時，本站將全額退款。</li>
        </ul>
      </Section>

      <Section title="第 2 條　法律依據">
        <p>
          依《<strong>消費者保護法</strong>》第 19 條第 1 項規定，通訊交易消費者得於收受商品或接受服務後 7 日內解除契約。
        </p>
        <p>
          惟同條第 2 項授權主管機關公告例外情形，據此行政院發布之《<strong>通訊交易解除權合理例外情事適用準則</strong>》第 2 條第 1 項第 5 款規定：
        </p>
        <p className="border-l-4 border-tarot-gold/40 pl-4 italic text-tarot-muted">
          「非以有形媒介提供之數位內容或一經提供即為完成之線上服務，經消費者事先同意始提供者，不適用消費者保護法第 19 條第 1 項規定。」
        </p>
        <p>
          本站提供之 AI 塔羅占卜服務，於您完成付款後即由系統即時運算並產出解讀結果，屬於前揭規範之「非以有形媒介提供之數位內容或一經提供即為完成之線上服務」，故<strong>不適用 7 天鑑賞期</strong>。
        </p>
      </Section>

      <Section title="第 3 條　付款流程之同意">
        <p>為符合主管機關「經消費者事先同意始提供」之要件，您於結帳頁面須勾選同意以下聲明：</p>
        <p className="border-l-4 border-tarot-gold/40 pl-4 italic">
          ☐ 我了解並同意：本網站提供之數位占卜服務，依《通訊交易解除權合理例外情事適用準則》第 2 條規定，屬於「非以有形媒介提供之數位內容或一經提供即為完成之線上服務」。本服務一經付款並提供占卜結果後，即不適用《消費者保護法》第 19 條之 7 天鑑賞期規定，無法辦理退費。
        </p>
        <p>未勾選者，系統將不允許您完成付款。本站將完整記錄您之同意時間、IP 位址及同意之政策版本，作為日後爭議之證據。</p>
      </Section>

      <Section title="第 4 條　可申請退費之例外情形">
        <p>下列情形本站願主動審酌退費：</p>
        <ul>
          <li><strong>系統錯誤</strong>：因本站系統故障導致已付費之占卜未能完成、或重複扣款</li>
          <li><strong>金流錯誤</strong>：金流商通知扣款異常</li>
          <li><strong>本站主動下架</strong>：尚未使用之預付額度於本站終止服務時退還</li>
          <li><strong>明顯違反廣告所示之服務瑕疵</strong>：經本站審核確認屬實</li>
        </ul>
        <p>退費方式：原路退回原付款工具，通常 7–14 個工作日到帳（依金流商作業時間）。</p>
      </Section>

      <Section title="第 5 條　不予退費之情形">
        <ul>
          <li>占卜已成功完成並產出結果</li>
          <li>已使用之訂閱服務期間</li>
          <li>因您個人不可抗力（如裝置損壞、無法上網）導致無法使用</li>
          <li>因您違反《服務條款》遭停權者</li>
          <li>對占卜結果之主觀感受不滿意（占卜結果之解讀本具主觀性）</li>
        </ul>
      </Section>

      <Section title="第 6 條　訂閱服務之取消">
        <ul>
          <li><strong>訂閱期內</strong>：您可隨時取消自動續訂，已扣款之當期服務仍可使用至期滿，<strong>不退還當期費用</strong>。</li>
          <li><strong>取消方式</strong>：登入後於「我的帳號 → 訂閱管理」自助操作，或來信申請。</li>
        </ul>
      </Section>

      <Section title="第 7 條　申請流程">
        <p>如符合第 4 條退費情形，請來信：📧 support@starstwinkling.com</p>
        <p>信中請附：</p>
        <ul>
          <li>您的註冊 Email</li>
          <li>訂單編號</li>
          <li>退費原因說明</li>
          <li>必要之證明（截圖、錯誤訊息）</li>
        </ul>
        <p>本站將於收信後 <strong>5 個工作日</strong> 內回覆審核結果。</p>
      </Section>

      <Section title="第 8 條　爭議處理">
        <p>若您對本站之退費決定有疑義，得依下列方式處理：</p>
        <ul>
          <li>向本站申訴</li>
          <li>向行政院消費者保護處或地方消費者服務中心申訴（<strong>消費者服務專線：1950</strong>）</li>
          <li>依法提起調解或訴訟</li>
        </ul>
      </Section>

      <Section title="第 9 條　政策修訂">
        <p>本政策得隨時修訂，已成立之訂單依訂單成立時之政策版本辦理。</p>
      </Section>
    </LegalPage>
  );
}
